import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import { LANG_en_gb, LANG_en_us } from 'c-lib';
import type { PcaReportFilters } from '../reportFilters';

type Props = {
    filters: PcaReportFilters | null | undefined;
    language: 'GB' | 'US';
    /** `stack`: caption over value (Linear cover); `grid`: label and value columns (DSP cover). */
    variant: 'stack' | 'grid';
};

/**
 * UL-8220: "Filtered by Media Owner: JCDecaux, Global · Environment: Roadside" on a PCA cover, so a
 * narrowed report can't be mistaken for the whole campaign. Nothing when the PCA isn't filtered.
 * Labels follow the report's own language (GB/US), not the viewer's.
 */
const ReportFiltersLine: React.FC<Props> = ({ filters, language, variant }) => {
    const t = useCommonTranslation();
    const entries = Object.entries(filters ?? {});
    if (entries.length === 0) return null;

    const lng = language === 'US' ? LANG_en_us : LANG_en_gb;
    const label = t('Modules.Main.Campaigns.Overview.Pca.Filters.filteredBy', { lng });
    const value = entries
        .map(
            ([key, values]) =>
                `${t(`Modules.Main.Campaigns.Overview.Reports.fields.${key}`, { lng })}: ${(
                    values ?? []
                ).join(', ')}`,
        )
        .join(' · ');

    if (variant === 'grid') {
        return (
            <>
                <Grid item xs={6}>
                    <Typography sx={{ fontWeight: 800, color: 'grey.700' }}>{label}:</Typography>
                </Grid>
                <Grid item xs={6}>
                    <Typography sx={{ fontWeight: 700 }} data-testid="pca-report-filters">
                        {value}
                    </Typography>
                </Grid>
            </>
        );
    }

    return (
        <Box>
            <Typography variant="caption" sx={{ color: 'grey.600' }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }} data-testid="pca-report-filters">
                {value}
            </Typography>
        </Box>
    );
};

export default ReportFiltersLine;
